import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CustomFilter } from 'app/ComponentLibrary/Filter';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import { IOrderCandidateMatchChips, defaultChips } from './store/orderCandidateMatch.redux';
import {
  OrderCandidateMatchFilterSpecs,
  preCandidateStatusOpts,
  getAllCitiesAndZips,
  getStatesArray,
} from './OrderCandidateMatchFilterUtils';
import { selectFilters, selectGridFiltersExpanded } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { getCitiesAndZipsByStates, getSearchFilterOptions } from 'app/services/SharedServices/SharedServices';
import { IStateCityZip } from 'app/models/Candidate/IStateCityZip';
import { getMultiSelectOptions } from '@AMIEWEB/GlobalSearch/helper';
import { trackPromise } from 'react-promise-tracker';
import { selectOrderDetailsUIView } from '@AMIEWEB/Order/Store/Order.selectors';
import { selectUser } from 'oidc/user.selectors';
import { Collapse, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { AMNDivisionType } from 'app/models/enums/AMNDivisionType';
import { initialCandidateAggregateSearch } from 'app/ComponentLibrary/Filter/CandidateSearch';
import { updateTreeNodes } from '@AMIEWEB/Common/TreeView/TreeViewHelpers';
import { convertToTreeStructure } from '@AMIEWEB/GlobalSearch/Order/FilterUtils';
import { isEqual } from 'lodash';
import { useDecision } from '@optimizely/react-sdk';
import { ff_candidate_leads_management } from 'app/constants/FeatureFlags/Candidate/Keys';

const useStyles = makeStyles()({
  fillterCollapse: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '3px',
  },
  collapse: {
    paddingTop: '4px',
  },
});

const OrderCandidateMatchFilters = props => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const dispatch = useDispatch();
  const gridStateFilters = useSelector(selectFilters);
  const allCitiesandZipsData = useRef<IStateCityZip[]>();
  const selectOrderUiDetails = useSelector(selectOrderDetailsUIView);
  const { userInfo } = useSelector(selectUser);
  const filtersExpanded = useSelector(selectGridFiltersExpanded);
  const [skillset, setSkillset] = useState(null);
  const oldSkillsets = useRef(null);
  const [leadsManagementFlag] = useDecision(ff_candidate_leads_management);

  const handleChange = () => {
    dispatch(gridStateActions.setFiltersExpanded(!filtersExpanded));
  };
  const gridSpecs = useRef(
    new OrderCandidateMatchFilterSpecs(
      {
        recruiterOpts: [],
        recruitingSpecialistOpts: [],
        brandOpts: [],
        candidateStatusOpts: [],
        disciplineOpts: [],
        specialtyOpts: [],
        statesOpts: [],
        cityOptions: [],
        zipOptions: [],
        licensesOptions: [],
        certificationOptions: [],
        certificationIssuers: [],
      },
      t,
      gridStateFilters,
      leadsManagementFlag?.enabled,
    ),
  );
  const [fields, setFields] = React.useState(gridSpecs.current.getOrderCandidateMatchFilters());

  const fetchFilterSpecs = async () => {
    getSearchFilterOptions('brands', true).then(res => {
      gridSpecs.current.filterSpecs.brandOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getOrderCandidateMatchFilters();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('candidatestatuses', true).then(res => {
      gridSpecs.current.filterSpecs.candidateStatusOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getOrderCandidateMatchFilters();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('disciplines', true).then(res => {
      gridSpecs.current.filterSpecs.disciplineOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getOrderCandidateMatchFilters();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('specialties', true).then(res => {
      gridSpecs.current.filterSpecs.specialtyOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getOrderCandidateMatchFilters();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('states', true).then(async res => {
      gridSpecs.current.filterSpecs.statesOpts = await getMultiSelectOptions(res || [], { withoutAllOption: true });
      const statesArray = getStatesArray(res || []);
      allCitiesandZipsData.current = (await getCitiesAndZipsByStates({
        states: [statesArray?.[0]],
        cities: undefined,
      })) as IStateCityZip[];
      if (!allCitiesandZipsData.current) return;
      const AllCitiesAndZips = getAllCitiesAndZips(allCitiesandZipsData.current);
      gridSpecs.current.filterSpecs.cityOptions = AllCitiesAndZips.cities;
      gridSpecs.current.filterSpecs.zipOptions = AllCitiesAndZips.zips;
      const fieldsSchema = gridSpecs.current.getOrderCandidateMatchFilters();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('licenseTypes', true).then(res => {
      gridSpecs.current.filterSpecs.licensesOptions = res?.map((license, id) => ({ id: id, ...license })) || [];
      const fieldsSchema = gridSpecs.current.getOrderCandidateMatchFilters();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('certificationTypes', true).then(res => {
      gridSpecs.current.filterSpecs.certificationOptions = res?.map((certificate, id) => ({
        id: id,
        ...certificate,
      }));
      const fieldsSchema = gridSpecs.current.getOrderCandidateMatchFilters();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('certificationIssuers', true).then(res => {
      gridSpecs.current.filterSpecs.certificationIssuers = res?.map((certificateIssuer, id) => ({
        ...certificateIssuer,
        id: id,
        value: certificateIssuer.name,
      }));
      const fieldsSchema = gridSpecs.current.getOrderCandidateMatchFilters();
      setFields(fieldsSchema);
    });
  };

  const updateGridState = useCallback(
    async (selectedStates: string[], selectedCities: string[], gridStateFilters: any) => {
      if (!gridStateFilters || !allCitiesandZipsData.current) return;
      let filteredZips: IStateCityZip[];
      let filteredCities: IStateCityZip[];
      if (!selectedStates.length) filteredCities = allCitiesandZipsData.current;
      else {
        filteredCities = (await getCitiesAndZipsByStates({
          states: selectedStates,
          cities: [],
        })) as IStateCityZip[];
      }

      if (!selectedCities.length) filteredZips = filteredCities;
      else {
        filteredZips = (await getCitiesAndZipsByStates({
          states: selectedStates,
          cities: selectedCities,
        })) as IStateCityZip[];
      }
      gridSpecs.current.filterSpecs.cityOptions = getAllCitiesAndZips(filteredCities).cities;
      gridSpecs.current.filterSpecs.zipOptions = getAllCitiesAndZips(filteredZips).zips;
      gridSpecs.current.gridStateFilters = gridStateFilters;
      const fieldsSchema = gridSpecs.current.getOrderCandidateMatchFilters();
      setFields(fieldsSchema);
    },
    [],
  );

  const getSkillSets = defaultSkillsets => {
    let newSkillSetFilter = convertToTreeStructure(defaultSkillsets);
    if (newSkillSetFilter?.length > 0) {
      newSkillSetFilter = updateTreeNodes(newSkillSetFilter);
      newSkillSetFilter[0].filterName = 'skillsetFilter';
      return {
        allowableMismatch: false,
        isVerifiedOnly: false,
        operation: 'Or',
        skillsets: newSkillSetFilter,
      };
    }
  };
  const newSkillsets = selectOrderUiDetails?.orderDetailsOrginal?.skillsets;
  const areSkillsetsChanged = !isEqual(oldSkillsets.current, newSkillsets);

  useEffect(() => {
    if (newSkillsets) {
      const defaultSkillsets = [...(newSkillsets || [])]
        .filter(item => !!item)
        .reduce((resultArray, item) => {
          if (!item?.disciplineId || !item?.disciplineAbbr) {
            return resultArray;
          }
          const newSkill = {
            disciplineId: item?.disciplineId,
            disciplineAbbr: item?.disciplineAbbr,
            specialtyId: item?.specialtyId,
            specialtyAbbr: item?.specialtyAbbr,
            subSpecialtyId: item?.subSpecialtyId,
            subSpecialtyAbbr: item?.subSpecialty,
          };
          resultArray?.push(newSkill);
          return resultArray;
        }, []);
      setSkillset(getSkillSets(defaultSkillsets));
      oldSkillsets.current = newSkillsets;
    }
  }, [areSkillsetsChanged]);

  useEffect(() => {
    if (!gridStateFilters || !allCitiesandZipsData.current) return;
    const selectedStates = (
      !(gridStateFilters?.filters?.states as any[])?.length ? [] : gridStateFilters?.filters?.states
    ) as any[];

    const selectedCities = (
      !(gridStateFilters?.filters?.city as any[])?.length ? [] : gridStateFilters?.filters?.city
    ) as any[];
    updateGridState(
      selectedStates.map(x => x.name),
      selectedCities.map(x => x.name),
      gridStateFilters,
    );
  }, [gridStateFilters, updateGridState]);

  const isRecruiter =
    userInfo.roles?.includes('Recruitment') ||
    userInfo.roles?.includes('Recruitment.Leadership') ||
    userInfo.roles?.includes('Recruitment.TeamMember');

  const matchingBrands = (props.brands ?? []).filter(brands => brands.value === userInfo?.companyId.toString());

  const brandHSGId = [
    {
      id: 4,
      value: '14',
      name: 'AMN Labor Disruption',
    },
  ];

  const isBrandHSG = selectOrderUiDetails?.headerDetails?.divisionId === AMNDivisionType.hsg;

  const userIds = [
    {
      id: userInfo.employeeId,
      name: userInfo.firstName + ' ' + userInfo.lastName,
      value: userInfo.employeeId.toString(),
    },
  ];

  const defaultAppliedFilters: IOrderCandidateMatchChips = {
    ...defaultChips,
    recruiterIds: isBrandHSG ? [] : isRecruiter ? userIds : [],
    candidateFilter: initialCandidateAggregateSearch,
    skillsetFilter: skillset,
    candidateStatusIds: preCandidateStatusOpts,
    brandIds: isBrandHSG ? brandHSGId : isRecruiter ? matchingBrands : [],
    disciplineIds: [],
    specialtyIds: [],
    activePlacements: {
      id: 0,
      name: 'Exclude',
      value: {
        include: false,
      },
    },
  };

  const resetFilter = () => {
    Object.entries(defaultAppliedFilters).map(item => setValue(item[0], item[1]));
    setValue('license', null);
    setValue('certification', null);
    setValue('workHistory', null);
    setValue('skillsetFilter', skillset);
    props.onApplyFilter(defaultAppliedFilters);
  };

  useEffect(() => {
    trackPromise(fetchFilterSpecs(), 'gloabl-search-call');
  }, []);

  useEffect(() => {
    if (skillset) resetFilter();
  }, [skillset]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={11}>
        <Collapse timeout={200} in={filtersExpanded} collapsedSize={45} className={classes.collapse}>
          <CustomFilter
            fields={fields}
            justifyContent={'flex-start'}
            version2
            resetBtn
            analytics={{
              caller: t('Order Candidate Match'),
              onApply: true,
              properties: {
                category: SearchType.candidate,
              },
            }}
          />
        </Collapse>
      </Grid>
      <Grid item xs={1} className={classes.fillterCollapse}>
        <SearchPageIcons
          resetlbutton
          funnelButton
          checked={filtersExpanded}
          onResetForm={() => resetFilter()}
          onAdvancedFilterClick={() => handleChange()}
        />
      </Grid>
    </Grid>
  );
};

export default OrderCandidateMatchFilters;
